<template>
  <div class="text-sm max-w-screen-md">
    <!-- Terms Content -->
    <div v-if="customTermsUrl" class="py-6">
      <Banner
        variant="info"
        bannerClass="text-sm w-full justify-between rounded-lg"
        :simple="true"
      >
        <div class="flex items-center justify-between w-full gap-4">
          <div class="flex items-center gap-3">
            <v-icon name="hi-download" />
            <span>Your account has custom terms of service.</span>
          </div>
          <XButton
            type="button"
            color="primary"
            @click="openCustomTerms"
          >
            View Terms PDF
          </XButton>
        </div>
      </Banner>
    </div>
    <TermsConditions v-else />


    <!-- Fixed Bottom Section - Teleport to layout's sticky area -->
    <Teleport to="#bottom-sticky" v-if="meResult?.me">
      <div class="max-w-screen-md pb-4 pt-4">
        <div class=" bg-base-100">
      <!-- Terms Agreed Status -->
      <div v-if="termsAgreed">
        <Banner
          variant="info"
          icon="bi-check-lg"
        >
          <span v-if="agreedToTermsBy && me.profile.companyName">
            Terms accepted by {{ agreedToTermsBy }} ({{ agreedToTermsByPosition }}) at {{ me.profile.companyName }} on {{ agreedDate }}.
          </span>
          <span v-else-if="agreedToTermsBy">
            Terms accepted by {{ agreedToTermsBy }} on {{ agreedDate }}.
          </span>
          <span v-else>
            Terms accepted on {{ agreedDate }}.
          </span>
        </Banner>
      </div>

      <!-- Agreement Form -->
      <div class="w-full" v-else>
        <Banner
          variant="info"
          bannerClass="text-sm w-full justify-between rounded-lg"
          :simple="true"
        >
        <form @submit.prevent="handleAgreedToTerms" class="flex flex-col w-full gap-4">
          <div class="flex flex-shrink items-center gap-3">
            <input
              id="agreed-to-terms"
              v-model="agreedToRead"
              type="checkbox"
              class="checkbox checkbox-primary text-white dark:text-primary-content checkbox-sm"
            />
            <div>
              <label for="agreed-to-terms" class="text-sm font-medium cursor-pointer">
                I agree to the Terms and Conditions
              </label>
              <p class="text-xs text-neutral-content/70 mt-1">
                By checking this box, I confirm that I have read and agree to the Terms and Conditions.
              </p>
            </div>
          </div>

          <div v-show="agreedToRead" class="flex w-full sm:w-auto min-w-48 md:min-w-[22rem] flex-col gap-3">
            <div class="flex flex-col sm:flex-row gap-1 sm:gap-3 text-xs">
              <FormProvider bgColor="sky-200 dark:base-100" borderColor="sky-300 dark:neutral" borderColorFocus="sky-400 dark:border-sky-700" borderColorHover="sky-400 dark:border-sky-700" labelColorFocus="sky-800 dark:text-sky-400">
                <XInput
                  v-model="agreedToTermsBy"
                  id="agreed-by"
                  label="Full Name"
                  type="text"
                  required
                  size="sm"
                  class="w-48"
                />
                <XInput
                  v-model="agreedToTermsByPosition"
                  id="agreed-position"
                  label="Company Position"
                  type="text"
                  required
                  size="sm"
                  class="w-40"
                />
            </FormProvider>
          </div>

          <XButton
            type="submit"
            color="primary"
            :isLoading="isLoadingUserUpdate"
            :isDisabled="!agreedToRead || !agreedToTermsBy || !agreedToTermsByPosition"
          >
            Accept Terms
          </XButton>
          </div>
          
        </form>
        </Banner>
      </div>
    </div>
      </div>
    
    </Teleport>

  </div>
</template>

<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue"
import { useQuery, useMutation } from "@vue/apollo-composable"
import { createToaster } from "@meforma/vue-toaster"
import { DateTime } from "luxon"
import { useRouter } from "vue-router"
import ME_QUERY from "@/graphql/User/MeQuery.gql"
import UPDATE_USER_MUTATION from "@/graphql/User/UpdateUserMutation.gql"
import TermsConditions from "./TermsConditions.vue"
import Banner from "@/components/common/Banner.vue"
import FormProvider from "@/components/common/FormProvider.vue"

const router = useRouter()
const toaster = createToaster({
  position: "bottom-right",
  duration: 4000,
})

// Reactive data
const termsAgreed = ref(false)
const agreedToRead = ref(false)
const agreedToTermsBy = ref(null)
const agreedToTermsByPosition = ref(null)
const validationErrors = ref(null)

// User data
const me = ref({
  profile: {
    companyName: null,
  },
})

// GraphQL queries and mutations
const { result: meResult, loading: isLoadingMe } = useQuery(ME_QUERY)
const { mutate: updateUser, loading: isLoadingUserUpdate } = useMutation(UPDATE_USER_MUTATION)

// Computed properties
const customTermsUrl = computed(() => {
  return meResult.value?.me?.userDetails?.customTermsUrl || null
})

const openCustomTerms = () => {
  window.open(customTermsUrl.value, '_blank')
}

const agreedDate = computed(() => {
  if (!meResult.value?.me?.userDetails?.agreedToTermsAt) return null
  return DateTime.fromFormat(
    meResult.value.me.userDetails.agreedToTermsAt,
    "yyyy-MM-dd HH:mm:ss",
  ).toFormat("LLLL d, yyyy")
})


// Methods
const handleAgreedToTerms = () => {
  const agreedBy = JSON.stringify({
    agreedBy: agreedToTermsBy.value,
    agreedByPosition: agreedToTermsByPosition.value || "",
  })

  updateUser({
    input: {
      id: meResult.value.me.id,
      userDetails: {
        update: {
          id: meResult.value.me.userDetails.id,
          agreedToTerms: true,
          agreedToTermsBy: agreedBy,
        },
      },
    },
  })
    .then(() => {
      toaster.show("Terms accepted successfully!", { type: "success" })
      termsAgreed.value = true
      router.push({ name: "Dashboard" })
    })
    .catch((error) => {
      console.error("Error accepting terms:", error)
      toaster.show("Error accepting terms. Please try again.", { type: "error" })
    })
}


watch(
  () => meResult.value,
  (result) => {
    if (result?.me) {
      const user = result.me
      me.value.profile.companyName = user.primaryContact?.company
      termsAgreed.value = user.userDetails.agreedToTermsAt ?? false

      const terms = JSON.parse(user.userDetails.agreedToTermsBy || "{}")
      agreedToTermsBy.value = terms.agreedBy
      agreedToTermsByPosition.value = terms.agreedByPosition
      
    }
  },
  { immediate: true }
)


</script>